import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  Grid,
  Alert,
  useTheme,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Schedule,
  Edit,
  Cancel,
  Email,
  Person,
  CalendarToday,
  PriorityHigh
} from '@mui/icons-material';

const PublicTicketTracker = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [ticketId, setTicketId] = useState('');
  const [email, setEmail] = useState('');
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!ticketId.trim() || !email.trim()) {
      setError('Please enter both Ticket ID and Email');
      return;
    }

    setLoading(true);
    setError('');
    setTicket(null);

    try {
      const response = await fetch(`https://yiyuz8psi0.execute-api.ap-south-1.amazonaws.com/prod/api/support/tickets/${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const foundTicket = data.tickets?.find(t => 
          t.ticketId.toLowerCase() === ticketId.toLowerCase().trim()
        );
        
        if (foundTicket) {
          setTicket(foundTicket);
        } else {
          setError('Ticket not found. Please check your Ticket ID and Email.');
        }
      } else {
        console.error('API Error:', response.status, response.statusText);
        setError('Unable to fetch ticket information. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching ticket:', error);
      setError('Unable to fetch ticket information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'error';
      case 'in-progress': return 'warning';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <Schedule />;
      case 'in-progress': return <Edit />;
      case 'resolved': return <CheckCircle />;
      case 'closed': return <Cancel />;
      default: return <Schedule />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getStatusTimeline = (ticket) => {
    const timeline = [
      {
        status: 'open',
        label: 'Ticket Created',
        date: ticket.createdAt,
        completed: true
      },
      {
        status: 'in-progress',
        label: 'In Progress',
        date: ticket.status === 'in-progress' || ticket.status === 'resolved' || ticket.status === 'closed' ? ticket.updatedAt : null,
        completed: ['in-progress', 'resolved', 'closed'].includes(ticket.status)
      },
      {
        status: 'resolved',
        label: 'Resolved',
        date: ticket.status === 'resolved' || ticket.status === 'closed' ? ticket.updatedAt : null,
        completed: ['resolved', 'closed'].includes(ticket.status)
      }
    ];
    return timeline;
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box
        sx={{
          textAlign: 'center',
          mb: 6,
          p: 4,
          borderRadius: 3,
          background: isDark
            ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 197, 253, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(255, 215, 0, 0.2)' : 'rgba(59, 130, 246, 0.2)'}`,
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            background: isDark
              ? 'linear-gradient(45deg, #FFD700, #FFA500)'
              : 'linear-gradient(45deg, #3B82F6, #93C5FD)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
        >
          Track Your Support Ticket
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Enter your ticket ID and email to check the status of your support request
        </Typography>
      </Box>

      {/* Search Form */}
      <Card
        sx={{
          mb: 4,
          background: isDark
            ? 'rgba(0, 0, 0, 0.3)'
            : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Find Your Ticket
          </Typography>
          <Box component="form" onSubmit={handleSearch}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Ticket ID"
                  placeholder="e.g., HC-123456-ABC"
                  value={ticketId}
                  onChange={(e) => setTicketId(e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                  disabled={loading}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(45deg, #FFD700, #FFA500)'
                      : 'linear-gradient(45deg, #3B82F6, #1E40AF)',
                    color: isDark ? '#000' : '#fff',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                  }}
                >
                  {loading ? 'Searching...' : 'Track Ticket'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Ticket Details */}
      {ticket && (
        <Card
          sx={{
            background: isDark
              ? 'rgba(0, 0, 0, 0.3)'
              : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                Ticket Details
              </Typography>
              <Chip
                icon={getStatusIcon(ticket.status)}
                label={ticket.status.replace('-', ' ').toUpperCase()}
                color={getStatusColor(ticket.status)}
                size="medium"
              />
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Ticket ID
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {ticket.ticketId}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Priority
                  </Typography>
                  <Chip
                    icon={<PriorityHigh />}
                    label={ticket.priority.toUpperCase()}
                    color={getPriorityColor(ticket.priority)}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Subject
                  </Typography>
                  <Typography variant="body1">
                    {ticket.subject}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Message
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {ticket.message}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday fontSize="small" />
                    <Typography variant="body1">
                      {new Date(ticket.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday fontSize="small" />
                    <Typography variant="body1">
                      {new Date(ticket.updatedAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              {ticket.assignedTo && (
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Assigned To
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person fontSize="small" />
                      <Typography variant="body1">
                        {ticket.assignedTo}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {ticket.resolution && (
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Resolution
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {ticket.resolution}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Status Timeline */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Progress Timeline
            </Typography>
            <List>
              {getStatusTimeline(ticket).map((item, index) => (
                <ListItem key={index} sx={{ py: 1 }}>
                  <ListItemIcon>
                    <Chip
                      icon={getStatusIcon(item.status)}
                      label={item.completed ? 'Done' : 'Pending'}
                      color={item.completed ? getStatusColor(item.status) : 'default'}
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    secondary={item.date ? new Date(item.date).toLocaleDateString() : 'Pending'}
                    primaryTypographyProps={{
                      fontWeight: item.completed ? 600 : 400,
                      color: item.completed ? 'text.primary' : 'text.secondary'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Help Section */}
      <Card
        sx={{
          mt: 4,
          background: isDark
            ? 'rgba(0, 0, 0, 0.3)'
            : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        }}
      >
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
            Need Additional Help?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            If you can't find your ticket or need immediate assistance, contact our support team directly.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Email />}
            href="mailto:contact.hypecircle@gmail.com"
            sx={{
              borderColor: isDark ? 'rgba(255, 215, 0, 0.5)' : 'rgba(59, 130, 246, 0.5)',
              color: isDark ? '#FFD700' : '#3B82F6',
              '&:hover': {
                borderColor: isDark ? '#FFD700' : '#3B82F6',
                backgroundColor: isDark ? 'rgba(255, 215, 0, 0.1)' : 'rgba(59, 130, 246, 0.1)',
              }
            }}
          >
            Contact Support
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PublicTicketTracker;